// Styled-components wrappers for Inside and Outside layouts
import styled, { css } from 'styled-components';
import { Layout } from 'antd';

import { colors, getInsideChrome, moduleItemSelectedBg, moduleSidebarSelectedAccent, moduleSidebarSelectedFill } from '@utils/colorSystem';

export const LayoutContainer = styled.div`
    width: 100%;
    max-width: 100vw;
    height: 100vh;
    overflow: hidden;
    box-sizing: border-box;
    position: relative;
    display: flex;
    flex-direction: column;
`;

/** Full-width top header (workspace + search + user); no line separator */
export const TopHeader = styled.header`
    flex-shrink: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px;
    box-sizing: border-box;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark
            ? 'linear-gradient(90deg, #1a1a1a 0%, #222 38%, #1a1a1a 72%, #181c22 100%)'
            : 'radial-gradient(ellipse 130% 200% at 0% 50%, rgba(255, 218, 185, 0.55) 0%, transparent 52%), radial-gradient(ellipse 95% 180% at 42% 20%, rgba(255, 185, 210, 0.38) 0%, transparent 48%), radial-gradient(ellipse 100% 190% at 58% 80%, rgba(230, 190, 255, 0.22) 0%, transparent 45%), radial-gradient(ellipse 120% 200% at 100% 50%, rgba(170, 210, 255, 0.55) 0%, transparent 52%), linear-gradient(90deg, #fff6ec 0%, #fdf5f8 32%, #f5f0fc 56%, #eef4ff 100%)';
    }};
    gap: 12px;
    min-height: 56px;
    @media (max-width: 768px) {
        min-height: 56px;
        padding: 10px 12px;
    }
`;

export const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

export const WorkspaceDropdown = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 12px;
    min-width: 140px;
    max-width: 340px;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? theme.colors.background.secondary : '#ffffff'};
    border-radius: 9999px;
    border: 1px solid
        ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
    font-weight: 500;
    font-size: 0.875rem;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.85)' : '#374151')};
    cursor: pointer;
    box-shadow: ${({ theme }) =>
        theme.colors.background.default === '#141414'
            ? '0 1px 2px rgba(0, 0, 0, 0.3)'
            : '0 1px 2px rgba(0, 0, 0, 0.05)'};
    &:hover {
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? theme.colors.background.tertiary : '#fafafa'};
    }
    .anticon {
        font-size: 12px;
        flex-shrink: 0;
    }
`;

export const WorkspaceAvatar = styled.div`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.75rem;
    flex-shrink: 0;

    span.main {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
        min-width: 0;
    }

    span.sub {
        fontWeight: 500,
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;
    }
`;

export const HeaderCenter = styled.div`
    flex: 1;
    max-width: 400px;
    margin: 0 auto;
`;

/** Search hotkey hint on the right (Mac: ⌘ K, Windows: Ctrl K) - no box */
export const SearchHotkeyHint = styled.span`
    font-size: 0.6875rem;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.45)' : '#9ca3af')};
    letter-spacing: 0.02em;
    margin-left: 4px;
`;

export const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
`;

export const AvatarWithBadge = styled.div`
    position: relative;
    .ant-badge-dot {
        top: 2px;
        right: 2px;
    }
`;

/** Row below header: module sidebar | feature sidebar | body */
export const BelowHeaderRow = styled.div`
    flex: 1;
    display: flex;
    min-height: 0;
    overflow: hidden;
`;

/** Module sidebar: icon + small label below; no hover on active; dark theme matches feature sidebar */
export const ModuleSidebarBlack = styled.div`
    flex-shrink: 0;
    width: ${({ $width }) => $width}px;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? theme.colors.background.default : '#1a1a1a'};
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 16px 16px;
    gap: 8px;
    overflow-y: auto;
    transition: width 0.2s;
    border-radius: 12px;
    margin: 8px;
    box-sizing: border-box;
    .ant-menu {
        background: transparent !important;
        border: none !important;
        width: 100% !important;
        padding: 0 !important;
        .ant-menu-item {
            width: calc(100% - 32px);
            min-height: 56px;
            height: auto;
            margin: 4px 16px;
            padding: 8px 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 4px;
            border-radius: 12px;
            color: rgba(255, 255, 255, 0.7);
            transition:
                color 0.2s ease,
                background-color 0.2s ease;
            text-align: center !important;
            box-sizing: border-box;
            &::after {
                display: none !important;
            }
            /* Inner wrapper (Ant Design) - center all content */
            > * {
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                justify-content: center !important;
                width: 100% !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            .ant-menu-item-icon {
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                margin: 0 auto !important;
                width: 100%;
            }
            /* SVGR sidebar icons: tint via color (see SidebarIconSvg.jsx) */
            .ant-menu-item-icon .sidebar-icon-svg {
                color: #ffffff;
            }
            .anticon {
                font-size: 22px;
                margin: 0 !important;
                display: block !important;
            }
            .ant-menu-title-content {
                display: block !important;
                font-size: 10px;
                line-height: 1.2;
                text-align: center !important;
                white-space: pre-line;
                max-width: 100%;
                width: 100%;
            }
            &:hover:not(.ant-menu-item-selected) {
                color: #fff !important;
                background: rgba(255, 255, 255, 0.06) !important;
                .anticon {
                    color: #fff !important;
                }
            }
            /* Selected: brand purple wash + icon (module column) */
            &.ant-menu-item-selected {
                background: ${moduleSidebarSelectedFill} !important;
                color: ${moduleSidebarSelectedAccent} !important;
                font-weight: 600;
                .anticon {
                    color: ${moduleSidebarSelectedAccent} !important;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${moduleSidebarSelectedAccent} !important;
                }
            }
            &.ant-menu-item-selected:hover {
                background: ${moduleSidebarSelectedFill} !important;
                color: ${moduleSidebarSelectedAccent} !important;
                .anticon {
                    color: ${moduleSidebarSelectedAccent} !important;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${moduleSidebarSelectedAccent} !important;
                }
            }
        }
    }
    @media (max-width: 1024px) {
        display: none;
    }
`;

/** Wraps primary module menu + "More" trigger in the black sidebar */
export const ModuleSidebarNavColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    flex: 1;
    min-height: 0;
    width: 100%;
`;

/** Matches module menu item styling; used for "More" */
export const ModuleSidebarMoreTrigger = styled.button`
    width: calc(100% - 32px);
    min-height: 56px;
    margin: 4px 16px;
    padding: 8px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    transition:
        color 0.2s ease,
        background-color 0.2s ease;
    box-sizing: border-box;

    .anticon {
        font-size: 22px;
        margin: 0;
        display: block;
    }

    span:last-child {
        font-size: 10px;
        line-height: 1.2;
        text-align: center;
        white-space: nowrap;
        max-width: 100%;
    }

    &:hover {
        color: #fff;
        background: rgba(255, 255, 255, 0.06);
        .anticon {
            color: #fff;
        }
    }

    ${({ $active }) =>
        $active &&
        `
        background: ${moduleSidebarSelectedFill};
        color: ${moduleSidebarSelectedAccent};
        .anticon { color: ${moduleSidebarSelectedAccent} !important; }
        &:hover {
            background: ${moduleSidebarSelectedFill} !important;
            color: ${moduleSidebarSelectedAccent} !important;
            .anticon { color: ${moduleSidebarSelectedAccent} !important; }
        }
    `}
`;

/** "More" in mobile drawer — aligns with MenuContainer list row style */
export const ModuleDrawerMoreTrigger = styled.button`
    width: 100%;
    padding: 12px 0 12px 16px;
    margin: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    border: none;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    font-size: 20px;
    font-weight: 400;
    color: #8a98a7;
    text-align: left;
    transition: background 0.2s ease, color 0.2s ease;

    .anticon {
        font-size: 24px;
        color: #8a98a7;
        flex-shrink: 0;
    }

    &:hover {
        color: #8a98a7;
        .anticon {
            color: #8a98a7;
        }
    }

    ${({ $active }) =>
        $active &&
        `
        background: #fff0f1;
        color: #f04a4a;
        font-weight: 600;
        .anticon { color: #f04a4a !important; }
    `}
`;

export const ModuleMorePopoverGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(108px, 1fr));
    gap: 8px;
    min-width: 248px;
    max-width: 320px;
`;

export const ModuleMoreGridTile = styled.button`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 8px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
    color: var(--ant-color-text);
    transition: background 0.2s ease, color 0.2s ease;
    text-align: center;

    .anticon {
        font-size: 22px;
    }

    span {
        font-size: 12px;
        line-height: 1.25;
        word-break: break-word;
    }

    &:hover {
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
    }

    ${({ $active }) =>
        $active &&
        `
        background: ${moduleSidebarSelectedFill};
        color: ${moduleSidebarSelectedAccent};
        .anticon { color: ${moduleSidebarSelectedAccent} !important; }
    `}
`;

/** Main content area (body) - takes remaining space */
export const MainContentArea = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
    background: ${({ theme }) => theme.colors.background.default};
`;

export const BodyContainer = styled.div`
    flex: 1;
    width: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    box-sizing: border-box;
`;

export const BodyScroll = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
    ${({ $flushBottom }) =>
        $flushBottom &&
        `
        padding-bottom: 0 !important;
        margin-bottom: 0 !important;
    `}
`;

export const ContentWrapper = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    max-width: 100%;
    padding: ${({ $noPadding }) => ($noPadding ? '0' : '8px')};
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => theme.colors.background.default};
    box-sizing: border-box;
    overflow: hidden;
`;

/** Inner body container (the box where we show data). Fixed height, does not grow; scroll lives inside. */
export const PageBodyContainer = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
`;

/** Scroll area inside the body container – only this area scrolls; body container height stays fixed. */
export const PageBodyScroll = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: ${({ $lockHeight }) => ($lockHeight ? 'hidden' : 'auto')};
    overflow-x: hidden;
    box-sizing: border-box;
    ${({ $flushBottom, theme }) =>
        $flushBottom &&
        `
        padding: 0 !important;
        margin: 0 !important;
        /* Match Sprints panel so layout grey does not show through short content */
        background: ${theme.colors.background.default === '#141414' ? theme.colors.background.secondary || '#1f1f1f' : '#ffffff'};
        /* Hide vertical scrollbar next to Kanban headers; wheel / touch / keyboard still scroll */
        scrollbar-width: none;
        -ms-overflow-style: none;
        &::-webkit-scrollbar {
            width: 0;
            height: 0;
        }
    `}

    ${({ $flushBottom, theme }) =>
        !$flushBottom &&
        `
    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: ${theme.colors.border.default};
        border-radius: 4px;
        &:hover {
            background: ${theme.colors.text.secondary};
        }
    }
    scrollbar-width: thin;
    scrollbar-color: ${theme.colors.border.default} transparent;
    `}
`;

export const HeaderWrapper = styled(Layout.Header)`
    background: ${({ theme, $isScrolled }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($isScrolled) {
            // Glassy effect with semi-transparent background
            return isDark ? 'rgba(41, 41, 41, 0.6)' : 'rgba(255, 255, 255, 0.5)';
        }
        return isDark ? '#292929' : '#f0f0f0';
    }} !important;
    padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xs}
        ${({ theme }) => theme.spacing.lg};
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: auto;
    min-height: auto;
    position: sticky;
    top: 0;
    z-index: 100;
    backdrop-filter: ${({ $isScrolled }) => ($isScrolled ? 'blur(20px) saturate(180%)' : 'none')};
    -webkit-backdrop-filter: ${({ $isScrolled }) => ($isScrolled ? 'blur(20px) saturate(180%)' : 'none')};
    transition:
        background 0.3s ease,
        backdrop-filter 0.3s ease,
        box-shadow 0.3s ease,
        border-color 0.3s ease;
    box-shadow: ${({ theme, $isScrolled }) => {
        if (!$isScrolled) return 'none';
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)';
    }};
    border-bottom: ${({ theme, $isScrolled }) => {
        if (!$isScrolled) return 'none';
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.06)';
    }};
    gap: ${({ theme }) => theme.spacing.md};
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding: 10px ${({ theme }) => theme.spacing.md};
        height: auto;
        min-height: auto;
    }
`;

export const HeaderContent = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    flex: 1;
    min-width: 0;
    justify-content: flex-start;

    @media (max-width: 1024px) {
        justify-content: center;
    }
`;

export const HeaderTitle = styled.h1`
    font-family: var(--font-family-geist);
    font-weight: var(--font-weight-geist-bold);
    font-size: 1.5rem;
    margin: 0;
    text-align: left;

    @media (max-width: 1024px) {
        text-align: center;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        font-size: 1.25rem;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
        font-size: 1.125rem;
    }
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    flex-shrink: 0;
    justify-content: ${({ $isMobile }) => ($isMobile ? 'flex-end' : 'flex-start')};

    @media (max-width: 1024px) {
        justify-content: flex-end;
    }
`;

/** Header icon button (e.g. notifications bell); active when on the related page */
export const HeaderIconButtonWrap = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 6px;
    border-radius: 10px;
    transition:
        background 0.2s,
        color 0.2s;
    color: ${({ theme }) => theme.colors.text?.secondary ?? 'rgba(255,255,255,0.65)'};
    ${({ $active, theme }) =>
        $active
            ? `
        background: rgba(139, 92, 246, 0.2);
        color: ${theme.colors.background?.primary ?? '#8b5cf6'};
        .anticon { color: ${theme.colors.background?.primary ?? '#8b5cf6'} !important; }
    `
            : ''}
    &:hover {
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'};
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#111')};
    }
    .ant-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
    }
    .anticon {
        font-size: 18px;
    }
`;

export const ThemeToggleButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.text.secondary};
    transition: color 0.2s;
    width: 22px;
    height: 22px;

    .anticon {
        font-size: 20px;
    }

    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
    }
`;

export const SearchInputWrapper = styled.div`
    position: relative;
    width: 100%;
    max-width: 400px;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        max-width: 300px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
        max-width: 200px;
    }

    .ant-input-affix-wrapper {
        cursor: pointer;
        border-radius: 9999px;
        min-height: 30px;
        padding: 2px 10px;
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? theme.colors.background.secondary : '#ffffff'};
        border-color: ${({ theme }) => theme.colors.border.default};
    }

    .ant-input-affix-wrapper .ant-input {
        font-size: 13px;
        line-height: 1.35;
    }

    .ant-input-affix-wrapper .ant-input-prefix {
        margin-inline-end: 6px;
    }

    .ant-input-affix-wrapper .ant-input-prefix .anticon {
        font-size: 14px;
        color: ${({ theme }) => theme.colors.text.secondary};
    }
`;

export const KeyboardShortcutContainer = styled.div`
    display: flex;
    gap: 2px;
    align-items: center;
`;

export const KeyboardShortcut = styled.span`
    background: ${({ theme }) => theme.colors.background.tertiary};
    border: 1px solid ${({ theme }) => theme.colors.border.default};
    border-radius: 3px;
    padding: 2px 6px;
    font-size: 0.75rem;
    font-family: monospace;
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: 1;
`;

export const SiderWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : theme.colors.background.default;
    }};
    border-right: 1px solid ${({ theme }) => theme.colors.border.default};
    transition: all 0.2s;
    padding: 0 ${({ theme }) => theme.spacing.md};
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    direction: rtl; /* RTL direction to position scrollbar on left */
    resize: none;

    /* Custom scrollbar styling - thin and light pink color, no arrows */
    &::-webkit-scrollbar {
        width: 2px !important;
        height: 100% !important;
        background: transparent !important;
        -webkit-appearance: none !important;
    }

    &::-webkit-scrollbar-track {
        background: transparent !important;
        border: none !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    &::-webkit-scrollbar-thumb {
        background: #fff0f1 !important;
        border-radius: 0 !important;
        cursor: pointer !important;
        border: none !important;
        min-height: 20px !important;

        &:hover {
            background: #fff0f1 !important;
            opacity: 0.8 !important;
        }
    }

    /* Completely remove scrollbar buttons (up/down arrows) */
    &::-webkit-scrollbar-button {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        -webkit-appearance: none !important;
        background: transparent !important;
        visibility: hidden !important;
    }

    &::-webkit-scrollbar-button:start:decrement,
    &::-webkit-scrollbar-button:end:increment {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        background: transparent !important;
        visibility: hidden !important;
    }

    &::-webkit-scrollbar-button:vertical:start:decrement,
    &::-webkit-scrollbar-button:vertical:end:increment {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        background: transparent !important;
        visibility: hidden !important;
    }

    &::-webkit-scrollbar-button:single-button {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
    }

    &::-webkit-scrollbar-button:double-button {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
    }

    &::-webkit-scrollbar-corner {
        display: none !important;
        background: transparent !important;
        visibility: hidden !important;
    }

    /* Firefox scrollbar - thin and light pink color, no arrows */
    scrollbar-width: thin;
    scrollbar-color: #fff0f1 transparent;

    /* Reset direction for all children to LTR */
    & > * {
        direction: ltr;
    }

    @media (max-width: 1024px) {
        display: none !important;
    }
`;

// Feature sidebar (second sidebar – module sub-nav; no separator line)
export const FeatureSidebarOuter = styled.div`
    flex-shrink: 0;
    width: ${({ $width }) => $width}px;
    background: ${({ theme }) => theme.colors.background.default};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: width 0.2s;
    @media (max-width: 1024px) {
        display: none;
    }
`;

export const FeatureSidebarWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${({ theme }) => theme.colors.background.default};
    padding: ${({ theme }) => theme.spacing.md} 0;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    & > * {
        direction: ltr;
    }
    .ant-menu {
        background: transparent !important;
        border-right: none !important;
        .ant-menu-item {
            border-radius: 8px;
            padding: 10px 16px !important;
            height: auto;
            line-height: 1.4;
            border-right: none !important;
            font-size: 0.875rem;
            /* Inactive: muted grey */
            color: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.5)' : '#9ca3af'};
            margin: 4px 16px;
            width: calc(100% - 32px);
            box-sizing: border-box;
            &::after {
                display: none !important;
            }
            .ant-menu-item-icon {
                width: 18px;
                min-width: 18px;
                height: 18px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                line-height: 1;
                margin-right: 10px;
            }
            .anticon {
                font-size: 18px;
                line-height: 1;
            }
            .ant-menu-item-icon.anticon {
                margin-right: 10px;
            }
            .ant-menu-item-icon .sidebar-icon-svg {
                color: inherit;
                width: 18px;
                height: 18px;
            }
            .ant-menu-title-content {
                margin-inline-start: 0 !important;
                min-width: 0;
                line-height: 1.4;
            }
            .ant-menu-item-icon + .ant-menu-title-content {
                margin-inline-start: 0 !important;
            }
            .project-tab-label {
                display: inline-block;
                line-height: 1.4;
                padding-left: 0;
                margin-left: 0;
            }
            /* Hover ↔ selected swap vs previous chrome */
            &:hover:not(.ant-menu-item-selected) {
                background: #fafafa !important;
                color: ${({ theme }) =>
                    theme.colors.background.default === '#141414' ? '#6b7280' : '#9ca3af'};
                .anticon {
                    color: inherit;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: inherit;
                }
            }
            &.ant-menu-item-selected {
                background: ${({ theme }) => getInsideChrome(theme).featureNavHoverBg} !important;
                color: ${({ theme }) => getInsideChrome(theme).featureNavHoverFg} !important;
                font-weight: 500;
                .anticon {
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverIcon} !important;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverIcon} !important;
                }
                &:hover {
                    background: ${({ theme }) => getInsideChrome(theme).featureNavHoverBg} !important;
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverFg} !important;
                }
                &:hover .anticon {
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverIcon} !important;
                }
                &:hover .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverIcon} !important;
                }
            }
        }
    }
`;

export const FeatureSidebarTitle = styled.div`
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.45)' : '#6b7280')};
    padding: 8px 20px;
    margin-bottom: 4px;
`;

/** Large project name heading in feature sidebar (project detail view) */
export const FeatureSidebarProjectName = styled.div`
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.35;
    letter-spacing: -0.01em;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.9)' : '#111827')};
    padding: 12px 16px 16px;
    margin-bottom: 4px;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#e5e7eb')};
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    font-family: var(--font-family-geist, inherit);
`;

/** Sidebar row: avatar + label/meta left, optional right content (e.g. icon), divider below */
export const FeatureSidebarLabelRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#e5e7eb')};
    font-family: var(--font-family-geist, inherit);
    background: transparent;
    transition: background 0.2s ease;
    width: 100%;
    cursor: pointer;
    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)')};
    }
`;
export const FeatureSidebarLabelRowContent = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
`;
export const FeatureSidebarLabelRowTextStack = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
    min-width: 0;
`;
export const FeatureSidebarLabelRowText = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.9)' : '#111827')};
    text-align: left;
    line-height: 1.3;
`;
export const FeatureSidebarLabelRowMeta = styled.span`
    font-size: 11px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.5)' : '#6b7280')};
    text-align: left;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

/** Expandable panel with smooth open/close motion */
export const FeatureSidebarChatExpandable = styled.div`
    overflow: hidden;
    max-height: ${({ $expanded }) => ($expanded ? '2000px' : '0')};
    transition: max-height 0.4s ease-in-out;
`;

/* Feature sidebar chat list (rich items with avatar + name + participants) */
export const FeatureSidebarChatScroll = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 12px 12px;

    /* Subtle scrollbar */
    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-thumb {
        border-radius: 999px;
        background: ${({ theme }) =>
            theme.colors.background.default === '#141414'
                ? 'rgba(255,255,255,0.12)'
                : 'rgba(0,0,0,0.12)'};
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
`;

export const FeatureSidebarChatItem = styled.button`
    width: ${({ $minimal, $flat }) => ($minimal && !$flat ? 'calc(100% - 18px)' : '100%')};
    display: flex;
    align-items: ${({ $minimal, $flat }) => ($minimal || $flat ? 'center' : 'flex-start')};
    gap: ${({ $minimal }) => ($minimal ? '8px' : '12px')};
    padding: ${({ $minimal, $flat }) =>
        $minimal || $flat ? '10px 0 10px 16px' : '12px 0 12px 14px'};
    border: none;
    border-radius: ${({ $minimal, $flat, $active }) => {
        if ($minimal && !$flat) return '10px 0 0 10px';
        if ($minimal && $flat) return $active ? '10px 0 0 10px' : '0';
        if (!$minimal && !$flat) return '10px';
        return '0';
    }};
    margin: ${({ $minimal, $flat }) => {
        if ($minimal && $flat) return '0';
        if ($minimal) return '4px 0 4px 18px';
        if ($flat) return '0';
        return '0 0 6px 0';
    }};
    background: ${({ $active, $minimal, $flat, theme }) => {
        if (($minimal || $flat) && !$active) return 'transparent';
        const dark = theme.colors.background.default === '#141414';
        if ($active) return dark ? 'rgba(139, 92, 246, 0.18)' : 'rgba(139, 92, 246, 0.1)';
        return dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
    }};
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.9)' : '#333')};
    text-align: left;
    cursor: pointer;
    transition: background 0.2s ease, box-shadow 0.2s ease;
    box-sizing: border-box;

    /* $flat: no divider; otherwise box with full border */
    border: ${({ $active, $minimal, $flat, theme }) => {
        if ($flat) return 'none';
        if ($minimal && !$active) return '1px solid transparent';
        const dark = theme.colors.background.default === '#141414';
        if ($active) return dark ? '1px solid rgba(139, 92, 246, 0.35)' : '1px solid rgba(139, 92, 246, 0.2)';
        return dark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)';
    }};

    &:hover {
        ${({ $minimal: _$minimal, $flat: _$flat, $active, theme }) => {
            if ($active) return css``;
            const dark = theme.colors.background.default === '#141414';
            return css`
                background: ${dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
                ${_$minimal &&
                _$flat &&
                css`
                    border-radius: 10px 0 0 10px;
                `}
            `;
        }}
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.colors.primary || 'var(--ant-color-primary)'};
        outline-offset: 2px;
        border-radius: ${({ $minimal, $flat, $active }) => {
            if ($minimal && !$flat) return '10px 0 0 10px';
            if ($minimal && $flat) return $active ? '10px 0 0 10px' : '10px 0 0 10px';
            if (!$minimal && !$flat) return '10px';
            return '10px 0 0 10px';
        }};
    }
`;

export const FeatureSidebarChatAvatar = styled.div`
    width: ${({ $size }) => $size || '36px'};
    height: ${({ $size }) => $size || '36px'};
    border-radius: ${({ $size }) => ($size === '28px' ? '999px' : '8px')};
    background: ${({ $avatarBg }) => $avatarBg || 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'};
    color: ${({ $active, $avatarColor }) => ($active ? '#6d28d9' : $avatarColor || 'white')};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: ${({ $size }) => ($size === '28px' ? '0.6875rem' : '0.8125rem')};
    flex-shrink: 0;
    .anticon {
        color: inherit;
    }
`;

export const FeatureSidebarChatInfo = styled.div`
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

export const FeatureSidebarChatName = styled.div`
    font-weight: 700;
    font-size: 14px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#111')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
`;

/** Title clamped to 2 lines with ellipsis. Use $active for primary color when parent item is selected (e.g. open market lead). */
export const FeatureSidebarChatNameTwoLines = styled.div`
    font-weight: 600;
    font-size: 13px;
    color: ${({ $active, theme }) =>
        $active ? '#6d28d9' : theme.colors.background.default === '#141414' ? '#fff' : '#111'};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.35;
`;

/** Status as colored text (e.g. below title) */
export const FeatureSidebarStatusText = styled.span`
    font-size: 12px;
    font-weight: 500;
    color: ${({ $color }) => $color || 'var(--ant-color-text-tertiary)'};
`;

export const FeatureSidebarChatProject = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.7)' : '#4b5563')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
`;

export const FeatureSidebarChatMeta = styled.div`
    font-size: 12px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#6b7280')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
`;

/** Channel-style: plain # character, no box */
export const FeatureSidebarChatChannelIcon = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme, $active }) =>
        $active
            ? theme.colors.background.default === '#141414'
                ? '#fff'
                : '#111'
            : theme.colors.background.default === '#141414'
                ? 'rgba(255,255,255,0.6)'
                : '#6b7280'};
    flex-shrink: 0;
`;

/** Channel list project name: small, regular; unread = bold + dark black, read = hint grey */
export const FeatureSidebarChatChannelName = styled.span`
    font-size: 13px;
    font-weight: ${({ $unread }) => ($unread ? 600 : 400)};
    color: ${({ $unread, $active, theme }) => {
        if ($active) return theme.colors.background.default === '#141414' ? '#fff' : '#111';
        if ($unread) return theme.colors.background.default === '#141414' ? '#fff' : '#111';
        return theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.5)' : '#6b7280';
    }};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
`;

export const FeatureSidebarChatDivider = styled.div`
    height: 1px;
    margin: 10px 0 8px;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
`;

export const FeatureSidebarChatSectionLabel = styled.div`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.4)' : '#9ca3af')};
    padding: 12px 12px 6px 16px;
    margin-top: 8px;
    user-select: none;
`;

/** Clickable accordion header: client name + arrow (expand/collapse) */
export const FeatureSidebarChatAccordionHeader = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px 8px 16px;
    margin: 4px 0 0;
    border: none;
    background: transparent;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.4)' : '#9ca3af')};
    text-align: left;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    font-family: inherit;

    &:hover {
        background: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)')};
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.6)' : '#6b7280')};
    }

    .accordion-arrow {
        flex-shrink: 0;
        transition: transform 0.2s;
    }
    &.expanded .accordion-arrow {
        transform: rotate(180deg);
    }
`;

/** Expandable accordion body (chats under a client) – smooth open/close, same feel as SprintTaskCard sprint header */
export const FeatureSidebarChatAccordionBody = styled.div`
    overflow: hidden;
    max-height: ${({ $expanded }) => ($expanded ? '2000px' : '0')};
    transition: max-height 0.9s ease-in-out;
`;

/** Feature sidebar item with icon + label + count – matches .ant-menu-item look (border-radius 8px, padding 10px 16px, margin 4px 12px, same colors/hover/selected) */
export const FeatureSidebarItemWithCount = styled.button`
    width: calc(100% - 18px);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 0 10px 12px !important;
    height: auto;
    line-height: 1.4;
    border: none;
    border-radius: 10px 0 0 10px;
    background: transparent;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.65)' : '#4b5563')};
    text-align: left;
    cursor: pointer;
    transition:
        color 0.2s,
        background 0.2s;
    margin: 4px 0 4px 18px;
    font-size: 0.875rem;
    &::after {
        display: none !important;
    }
    .anticon {
        font-size: 18px;
        margin-right: 10px;
        flex-shrink: 0;
    }
    &:hover {
        ${({ $active, theme }) =>
            !$active &&
            css`
                color: ${getInsideChrome(theme).featureNavHoverFg};
                background: ${getInsideChrome(theme).featureNavHoverBg};
            `}
    }
    ${({ $active }) =>
        $active
            ? `
        background: ${moduleItemSelectedBg};
        color: ${colors.primary[600]};
        font-weight: 600;
        .anticon { color: ${colors.primary[600]}; }
    `
            : ''}
`;
export const FeatureSidebarItemLabel = styled.span`
    flex: 1;
    min-width: 0;
    font-weight: inherit;
`;
export const FeatureSidebarItemCount = styled.span`
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    min-width: 18px;
    height: 18px;
    padding: 0 6px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: ${({ theme }) => theme.colors.primary || '#7E57C2'};
`;

export const StickyTopSection = styled.div`
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : theme.colors.background.default;
    }};
    padding-bottom: ${({ theme }) => theme.spacing.md};
`;

/* Generic panel header (reusable for chat, detail panels, etc.) */
export const PanelHeaderWrap = styled.div`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#eee')};
    background: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#1f1f1f' : '#fff')};
`;

export const PanelHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 20px 8px;
    gap: 12px;
    min-height: 0;
`;

export const PanelHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    flex: 1;
`;

export const PanelHeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
`;

export const AvatarInitials = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${({ $avatarBg }) => $avatarBg || 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'};
    color: ${({ $avatarColor }) => $avatarColor || '#fff'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.9375rem;
    flex-shrink: 0;
    cursor: default;
`;

export const PanelTitle = styled.span`
    font-weight: 600;
    font-size: 16px;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#111')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const IconButton = styled.button`
    width: 36px;
    height: 36px;
    border: none;
    background: transparent;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.6)' : '#666')};
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    &:hover {
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#111')};
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
    }
`;

export const TabsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0 20px 12px;
`;

export const TabItem = styled.button`
    padding: 6px 12px;
    border: none;
    background: transparent;
    color: ${({ theme, $active }) =>
        theme.colors.background.default === '#141414'
            ? $active
                ? '#fff'
                : 'rgba(255,255,255,0.55)'
            : $active
                ? '#111'
                : '#666'};
    font-size: 14px;
    font-weight: ${({ $active }) => ($active ? 600 : 500)};
    cursor: pointer;
    border-radius: 6px;
    border-bottom: ${({ $active }) => ($active ? '2px solid #8b5cf6' : '2px solid transparent')};
    margin-bottom: -2px;
    &:hover {
        color: ${({ theme }) => (theme.colors.background.default === '#141414' ? '#fff' : '#111')};
    }
`;

export const LogoContainer = styled.div`
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.md} 0;
    display: flex;
    align-items: start;
    justify-content: flex-start;
    gap: ${({ theme }) => theme.spacing.sm};
    min-height: 50px;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding: ${({ theme }) => theme.spacing.md};
        min-height: 56px;
    }
`;

export const LogoImage = styled.img`
    height: 50px;
    width: 125px;
    object-fit: contain;
    margin-top: ${({ theme }) => theme.spacing.md};

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        height: 50px;
        width: 125px;
    }
`;

export const UserProfileSection = styled.div`
    padding: 0 20px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
    gap: ${({ theme, $collapsed }) => ($collapsed ? 0 : theme.spacing.md)};
    background: #f0f0f0;
    border: 1px solid #dce1e5;
    border-radius: 8px;
    margin-top: 35px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-sizing: border-box;

    &:hover {
        background: #f0f0f0;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding: 0 20px;
        height: 64px;
    }
`;

export const UserInfoWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    flex: 1;
    min-width: 0;
    overflow: hidden;
    opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
    max-width: ${({ $collapsed }) => ($collapsed ? 0 : '100%')};
    transition:
        opacity 0.3s ease,
        max-width 0.3s ease,
        gap 0.3s ease;
`;

export const DropdownIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: inherit;
    font-size: 12px;
    flex-shrink: 0;
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    transition: transform 0.3s ease;

    .anticon {
        font-size: 12px;
    }
`;

export const UserAvatar = styled.div`
    width: ${({ $size }) => $size || '40px'};
    height: ${({ $size }) => $size || '40px'};
    border-radius: ${({ $radius }) => $radius || '50%'};
    background: ${({ $avatarBg, theme }) => $avatarBg || theme.colors.primary};
    color: ${({ $avatarColor }) => $avatarColor || 'white'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: ${({ $collapsed, $size }) =>
        $size === '24px' || $size === '32px' ? '0.75rem' : $collapsed ? '0.875rem' : '1rem'};
    flex-shrink: 0;
    transition: all 0.3s ease;
    overflow: hidden;
`;

export const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
`;

export const UserName = styled.div`
    font-weight: 600;
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.text.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const UserRole = styled.div`
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colors.text.secondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

/* User dropdown panel (avatar menu in header) */
export const UserDropdownPanel = styled.div`
    min-width: 300px;
    width: 320px;
    border-radius: 12px;
    overflow: hidden;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? theme.colors.background.secondary : '#ffffff'};
    border: 1px solid
        ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
    box-shadow: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? '0 12px 40px rgba(0,0,0,0.4)' : '0 12px 40px rgba(0,0,0,0.12)'};
`;

export const UserDropdownHeader = styled.div`
    padding: 20px 20px 16px;
    display: flex;
    align-items: center;
    gap: 14px;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
`;

export const UserDropdownAvatar = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: ${({ $avatarBg }) => $avatarBg || 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)'};
    color: ${({ $avatarColor }) => $avatarColor || 'white'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 1rem;
    flex-shrink: 0;
    letter-spacing: 0.02em;
    overflow: hidden;
`;

export const UserDropdownInfo = styled.div`
    min-width: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const UserDropdownName = styled.div`
    font-weight: 600;
    font-size: 1rem;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.95)' : '#111827')};
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const UserDropdownMeta = styled.div`
    font-size: 0.8125rem;
    color: ${({ theme }) => (theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.55)' : '#6b7280')};
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const UserDropdownDivider = styled.div`
    height: 1px;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'};
    margin: 0;
`;

export const UserDropdownMenuWrap = styled.div`
    padding: 8px 0;
    .ant-dropdown-menu {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
        min-width: unset !important;
    }
    .ant-dropdown-menu-item {
        padding: 10px 20px !important;
        margin: 0 8px;
        border-radius: 8px;
        font-size: 0.9375rem;
        font-weight: 500;
        color: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.88)' : '#374151'} !important;
        transition: background 0.15s ease;
    }
    .ant-dropdown-menu-item:hover {
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'} !important;
    }
    .ant-dropdown-menu-item-danger {
        color: ${({ theme }) => theme.colors.error || '#dc2626'} !important;
    }
    .ant-dropdown-menu-item-danger:hover {
        background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? 'rgba(220,38,38,0.12)' : 'rgba(220,38,38,0.06)'} !important;
    }
    .ant-dropdown-menu-item .anticon {
        margin-right: 10px;
        font-size: 1rem;
    }
`;

export const MenuContainer = styled.div`
    margin-top: 40px;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: ${({ theme }) => theme.spacing.md};

    .ant-menu {
        background: transparent;
        border-right: none !important;

        .ant-menu-item {
            border-radius: 6px;
            padding: 12px 0 12px 16px !important;
            height: auto;
            line-height: 1.5;
            border-right: none !important;
            font-size: 20px;
            font-weight: 400;
            color: #8a98a7;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 12px; /* Gap between icon and text */

            &::after {
                display: none !important;
            }

            .anticon {
                font-size: 24px; /* Reduced icon size */
                color: #8a98a7;
                margin-right: 0;
            }

            &:hover {
                background: transparent;
                color: #8a98a7;

                .anticon {
                    color: #8a98a7;
                }
            }

            &.ant-menu-item-selected {
                background: #fff0f1;
                color: #f04a4a;
                font-weight: 600;
                border-right: none !important;

                &::after {
                    display: none !important;
                }

                .anticon {
                    color: #f04a4a;
                    font-size: 24px;
                }
            }
        }
    }
`;

/** Mobile Drawer: feature/project sidebar section (match desktop feature sidebar look). */
export const DrawerFeatureSection = styled.div`
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid ${({ theme }) => theme.colors.border.default};

    .drawer-feature-title {
        font-size: 0.6875rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: ${({ theme }) =>
            theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.45)' : '#6b7280'};
        padding: 0 6px 8px;
        user-select: none;
    }

    .drawer-feature-project-name {
        font-size: 1.05rem;
        font-weight: 650;
        line-height: 1.25;
        letter-spacing: -0.01em;
        color: ${({ theme }) =>
            theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.92)' : '#111827'};
        padding: 0 6px 10px;
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        font-family: var(--font-family-geist, inherit);
    }

    .ant-menu {
        background: transparent !important;
        border-right: none !important;
        border-inline-end: none !important;
        padding: 0 !important;

        .ant-menu-item {
            border-radius: 10px;
            padding: 10px 16px !important;
            height: auto;
            line-height: 1.35;
            font-size: 0.95rem;
            margin: 4px 16px;
            width: calc(100% - 32px);
            box-sizing: border-box;
            border-right: none !important;
            border-inline-end: none !important;
            color: ${({ theme }) =>
                theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.65)' : '#4b5563'};

            &::after {
                display: none !important;
            }

            .anticon {
                font-size: 18px;
                margin-right: 10px;
                color: inherit;
            }

            .ant-menu-item-icon .sidebar-icon-svg {
                color: inherit;
            }

            &:hover:not(.ant-menu-item-selected) {
                background: #fafafa !important;
                color: ${({ theme }) =>
                    theme.colors.background.default === '#141414' ? '#6b7280' : '#9ca3af'};
                .anticon {
                    color: inherit;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: inherit;
                }
            }

            &.ant-menu-item-selected {
                background: ${({ theme }) => getInsideChrome(theme).featureNavHoverBg} !important;
                color: ${({ theme }) => getInsideChrome(theme).featureNavHoverFg} !important;
                font-weight: 500;
                .anticon {
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverIcon} !important;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverIcon} !important;
                }
                &:hover {
                    background: ${({ theme }) => getInsideChrome(theme).featureNavHoverBg} !important;
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverFg} !important;
                }
                &:hover .anticon {
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverIcon} !important;
                }
                &:hover .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverIcon} !important;
                }
            }
        }
    }
`;

export const CollapseButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: ${({ theme }) => theme.spacing.sm};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.text.secondary};
    transition: all 0.2s;
    border-radius: 8px;
    width: 32px;
    height: 32px;
    flex-shrink: 0;

    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
        background: ${({ theme }) => theme.colors.background.tertiary};
    }

    .anticon {
        font-size: 18px;
    }
`;

export const HeaderCollapseButton = styled.button`
    background: transparent;
    border: 1px solid
        ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    transition: all 0.2s;
    border-radius: 8px;
    width: 32px;
    height: 32px;
    flex-shrink: 0;

    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
        background: transparent;
    }

    .anticon {
        font-size: 16px;
    }
`;

export const SidebarCollapseButton = styled.button`
    width: 100%;
    margin-top: 20px;
    padding: 12px;
    background: transparent;
    border: 1px solid
        ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#2C2C2C' : '#E5E5E5';
    }};
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    transition: all 0.2s;

    &:hover {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#2C2C2C' : '#F5F5F5';
    }};
        border-color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    }

    svg {
        width: 16px;
        height: 16px;
    }

    @media (max-width: 1024px) {
        display: none;
    }
`;

export const MobileDrawerButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }};
    transition: all 0.2s;
    border-radius: 8px;
    font-size: 20px;

    &:hover {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
    }};
    }

    &:active {
        transform: scale(0.95);
    }

    @media (min-width: 1025px) {
        display: none;
    }
`;

export const DrawerOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    transition:
        opacity 0.3s ease,
        visibility 0.3s ease;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);

    @media (min-width: 1025px) {
        display: none;
    }
`;

export const MobileDrawer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1000;
    transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
    width: 100%;

    @media (min-width: 769px) and (max-width: 1024px) {
        width: 32.5% !important;
    }

    @media (max-width: 768px) {
        width: 100%;
    }

    @media (min-width: 1025px) {
        display: none;
    }
`;

export const DrawerContent = styled.div`
    height: 100%;
    width: 100%;
    min-height: 0;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#1a1a1a' : '#f4f4f5';
    }};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0;
    box-sizing: border-box;
`;

export const DrawerHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-shrink: 0;
    padding: 16px 16px 14px;
    margin: 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#1a1a1a' : '#ffffff';
    }};

    ${LogoImage} {
        height: 50px;
        width: 125px;
        object-fit: contain;
        margin: 0;
    }
`;

/** Mobile Drawer: render both sidebars like desktop (black module bar + feature sidebar). */
export const DrawerSidebarRow = styled.div`
    display: flex;
    align-items: stretch;
    gap: 10px;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    margin: 0;
    padding: 12px 12px 16px;
    box-sizing: border-box;
`;

/** Same look as `ModuleSidebarBlack` but allowed on mobile drawer. */
export const DrawerModuleSidebarBlack = styled.div`
    flex-shrink: 0;
    width: ${({ $width }) => $width}px;
    background: ${({ theme }) =>
        theme.colors.background.default === '#141414' ? theme.colors.background.default : '#1a1a1a'};
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 14px 16px;
    gap: 8px;
    overflow-y: auto;
    border-radius: 12px;
    box-sizing: border-box;

    .ant-menu {
        background: transparent !important;
        border: none !important;
        width: 100% !important;
        padding: 0 !important;

        .ant-menu-item {
            width: calc(100% - 32px);
            min-height: 56px;
            height: auto;
            margin: 4px 16px;
            padding: 8px 0 !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 4px;
            border-radius: 12px;
            color: rgba(255, 255, 255, 0.7);
            transition:
                color 0.2s ease,
                background-color 0.2s ease;
            text-align: center !important;
            box-sizing: border-box;

            &::after {
                display: none !important;
            }

            > * {
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                justify-content: center !important;
                width: 100% !important;
                margin: 0 !important;
                padding: 0 !important;
            }

            .ant-menu-item-icon {
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                margin: 0 auto !important;
                width: 100%;
            }

            .ant-menu-item-icon .sidebar-icon-svg {
                color: #ffffff;
            }

            .anticon {
                font-size: 22px;
                margin: 0 !important;
                display: block !important;
            }

            .ant-menu-title-content {
                display: block !important;
                font-size: 10px;
                line-height: 1.2;
                text-align: center !important;
                white-space: pre-line;
                max-width: 100%;
                width: 100%;
            }

            &:hover:not(.ant-menu-item-selected) {
                color: #fff !important;
                background: rgba(255, 255, 255, 0.06) !important;
                .anticon {
                    color: #fff !important;
                }
            }

            &.ant-menu-item-selected {
                background: ${moduleSidebarSelectedFill} !important;
                color: ${moduleSidebarSelectedAccent} !important;
                font-weight: 600;
                .anticon {
                    color: ${moduleSidebarSelectedAccent} !important;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${moduleSidebarSelectedAccent} !important;
                }
            }
            &.ant-menu-item-selected:hover {
                background: ${moduleSidebarSelectedFill} !important;
                color: ${moduleSidebarSelectedAccent} !important;
                .anticon {
                    color: ${moduleSidebarSelectedAccent} !important;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${moduleSidebarSelectedAccent} !important;
                }
            }
        }
    }
`;

/** Mobile Drawer: right feature sidebar panel (same feel as desktop sidebar). */
export const DrawerFeatureSidebarPanel = styled.div`
    flex: 1;
    min-width: 0;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? theme.colors.background.default : '#ffffff';
    }};
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border: 1px solid
        ${({ theme }) => {
            const isDark = theme.colors.background.default === '#141414';
            return isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb';
        }};
    box-shadow: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.04)';
    }};
`;

export const DrawerFeatureSidebarScroll = styled.div`
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: ${({ theme }) => theme.spacing.md} 0;

    .drawer-feature-title {
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: ${({ theme }) =>
            theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.45)' : '#6b7280'};
        padding: 8px 20px;
        margin-bottom: 4px;
    }

    .drawer-feature-project-name {
        font-size: 1.125rem;
        font-weight: 600;
        line-height: 1.35;
        letter-spacing: -0.01em;
        color: ${({ theme }) =>
            theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.9)' : '#111827'};
        padding: 12px 16px 16px;
        margin-bottom: 4px;
        border-bottom: 1px solid ${({ theme }) => (theme.colors.background.default === '#141414' ? '#2C2C2C' : '#e5e7eb')};
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        font-family: var(--font-family-geist, inherit);
    }

    .ant-menu {
        background: transparent !important;
        border-right: none !important;

        .ant-menu-item {
            border-radius: 8px;
            padding: 10px 16px !important;
            height: auto;
            line-height: 1.4;
            border-right: none !important;
            font-size: 0.875rem;
            color: ${({ theme }) =>
                theme.colors.background.default === '#141414' ? 'rgba(255, 255, 255, 0.5)' : '#9ca3af'};
            margin: 4px 16px;
            width: calc(100% - 32px);
            box-sizing: border-box;
            &::after {
                display: none !important;
            }
            .anticon {
                font-size: 18px;
                margin-right: 10px;
            }
            .ant-menu-item-icon .sidebar-icon-svg {
                color: inherit;
            }
            &:hover:not(.ant-menu-item-selected) {
                background: #fafafa !important;
                color: ${({ theme }) =>
                    theme.colors.background.default === '#141414' ? '#6b7280' : '#9ca3af'};
                .anticon {
                    color: inherit;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: inherit;
                }
            }
            &.ant-menu-item-selected {
                background: ${({ theme }) => getInsideChrome(theme).featureNavHoverBg} !important;
                color: ${({ theme }) => getInsideChrome(theme).featureNavHoverFg} !important;
                font-weight: 500;
                .anticon {
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverIcon} !important;
                }
                .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverIcon} !important;
                }
                &:hover {
                    background: ${({ theme }) => getInsideChrome(theme).featureNavHoverBg} !important;
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverFg} !important;
                }
                &:hover .anticon {
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverIcon} !important;
                }
                &:hover .ant-menu-item-icon .sidebar-icon-svg {
                    color: ${({ theme }) => getInsideChrome(theme).featureNavHoverIcon} !important;
                }
            }
        }
    }
`;

export const DrawerCloseButton = styled.button`
    background: transparent;
    border: 1px solid
        ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    transition: all 0.2s;
    border-radius: 8px;
    width: 36px;
    height: 36px;
    flex-shrink: 0;

    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
    }};
        border-color: ${({ theme }) => theme.colors.text.primary};
    }

    .anticon {
        font-size: 18px;
    }
`;

export const OutsideContainer = styled.div`
    min-height: 100vh;
    height: 100vh;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    justify-content: center;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : theme.colors.background.default;
    }};
    padding: 0;
`;

export const OutsideLayoutContent = styled(Layout.Content)`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    min-height: min-content;
    padding: 0;
    background: transparent;
`;

export const OutsideContent = styled.div`
    display: grid;
    grid-template-columns: ${({ $centered }) => ($centered ? '1fr' : '1.2fr 1fr')};
    width: 100%;
    gap: ${({ theme }) => theme.spacing.xl};
    min-height: calc(100vh - ${({ theme }) => theme.spacing.xl} * 2);
    justify-content: center;
    align-items: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
        position: relative;
        display: block;
        grid-template-columns: none;
        gap: 0;
        min-height: 100vh;
    }
`;
